import { AxiosResponse } from 'axios';
import React from 'react';
import useLoading from './useLoading';
import { IField, useField } from './useField';
import { IEnumRecord } from './useRequest';
import { MenuItem } from '@material-ui/core';

type Load<TSet> = (loadFn: () => Promise<AxiosResponse<TSet[]>>, afterLoad?: (response: AxiosResponse<TSet[]>) => void) => void;

export type ComboParams<TSet> = {
    set?: TSet[],
    value?: any
}

export interface ICombo<TSet extends object> {
    field: IField
    set: TSet[]
    updateSet: (newSet: TSet[]) => void
    handleResponse: (response: AxiosResponse<TSet[]>) => void
    load: Load<TSet>
}

export type AfterLoadEventHandler<TSet> = (response: AxiosResponse<TSet[]>) => void;
export type CreateOptionsParams = {
    prefix?: string
    sufix?: string
}

export interface ISpecializedCombo<TSet extends object> extends ICombo<TSet> {
    createOptions: (params?: CreateOptionsParams) => JSX.Element[]
    loadOptions: (afterLoad?: AfterLoadEventHandler<TSet>) => void
}

export interface IEnumCombo<TEnum> extends ISpecializedCombo<IEnumRecord<TEnum>> {
}

export function useEnumCombo<TEnum extends number>(loadFn: () => Promise<AxiosResponse<IEnumRecord<TEnum>[]>>) : IEnumCombo<TEnum> {
    const combo = useCombo<IEnumRecord<TEnum>>();

    const loadOptions = React.useCallback(() => {
        combo.load(loadFn);
    }, [combo, loadFn]);

    const createOptions = React.useCallback(() => {
        return combo.set.map((enumRecord, index) => {
            return <MenuItem key={index} value={enumRecord.value}>{ enumRecord.description }</MenuItem>
        });
    }, [combo.set]);
    
    return React.useMemo(() => ({
        ...combo,
        loadOptions,
        createOptions
    }), [combo, loadOptions, createOptions]);    
}

export function useCombo<TSet extends object>(initialState?: ComboParams<TSet>) : ICombo<TSet> {
    const [_set, _setSet] = React.useState<TSet[]>(initialState?.set || []);
    
    const field = useField("", initialState?.value);
    const loading = useLoading();

    const updateSet = React.useCallback((newSet: TSet[]) => {
        _setSet(newSet);
    }, []);

    const handleResponse = React.useCallback((response: AxiosResponse<TSet[]>) => {
        _setSet(response.data);
    }, []);

    const load = React.useCallback((loadFn: () => Promise<AxiosResponse<TSet[]>>, afterLoad?: AfterLoadEventHandler<TSet>) => {
        if (!loading.isLoading) {
            loading.start();
            loadFn()
                .then(response => {
                    handleResponse(response);
                    if (afterLoad !== undefined) {
                        afterLoad(response);
                    }
                })
                .catch(e => {})
                .finally(loading.stop);
        }
    }, [handleResponse, loading]);

    return React.useMemo(() => ({
        field, set: _set, updateSet, handleResponse, load
    }), [_set, field, handleResponse, load, updateSet]);
}